"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.createProjectsEditDialog = createProjectsEditDialog;
var _dialog = require("../../ui/dialog");
var _litHtml = require("lit-html");
require("../../styles/EditDialogs.css");
require("../../styles/ContactInfoEditDialog.css");
var _rowState = require("../shared/rowState");
var _textUtils = require("../../textUtils");
var _mutations = require("./mutations");
var _profileIcons = require("../../icons-svg/profileIcons");
var _texts = require("../../texts");
function sanitizeProjectFieldValue(value) {
  return (0, _textUtils.sanitizeTextValue)(value);
}
function isValidProjectUrl(value) {
  const text = sanitizeProjectFieldValue(value);
  if (!text) return false;
  try {
    const parsed = new URL(text);
    return parsed.protocol === 'https:' && parsed.pathname === '/profile/card' && parsed.hash === '#me';
  } catch {
    return false;
  }
}
function rowHasContent(row) {
  return [row.url, row.entryNode].some(_textUtils.hasNonEmptyText);
}
async function pasteWebIdIntoRow(row) {
  const clipboardText = await navigator.clipboard.readText();
  const url = sanitizeProjectFieldValue(clipboardText);
  if (!url) return '';
  (0, _rowState.applyRowFieldChange)(row, 'url', url, rowHasContent);
  return url;
}
function toFormState(_details) {
  return {
    project: {
      url: '',
      entryNode: '',
      status: 'new'
    }
  };
}
function validateProjectBeforeSave(row) {
  if (!(0, _textUtils.hasNonEmptyText)(row.url) && !(0, _textUtils.hasNonEmptyText)(row.entryNode)) {
    return 'Add a project WebID.';
  }
  if (!(0, _textUtils.hasNonEmptyText)(row.url)) {
    return 'WebID is required.';
  }
  if (!isValidProjectUrl(row.url)) {
    return 'Please enter a valid WebID in the form https://example.com/profile/card#me.';
  }
  return null;
}
function renderProjectInputRow(row, onChange) {
  const projectUrl = 'project-0';
  const handleUrlInput = e => {
    const target = e.target;
    const nextValue = sanitizeProjectFieldValue(target.value);
    (0, _rowState.applyRowFieldChange)(row, 'url', nextValue, rowHasContent);
  };
  const handlePaste = async event => {
    event.preventDefault();
    try {
      const url = await pasteWebIdIntoRow(row);
      onChange();
      if (!isValidProjectUrl(url)) return;
      const dom = event.currentTarget?.ownerDocument || document;
      const saveButton = dom.querySelector('#profile-modal #modal-buttons .btn-primary');
      saveButton?.click();
    } catch {
      // Clipboard access may fail due to browser permissions; keep dialog usable.
    }
  };
  return (0, _litHtml.html)`
    <div class="profile-edit-dialog__row profile-edit-dialog__row--project">
      <label aria-label="Project or community WebID" class="label profile-edit-dialog__field profile-edit-dialog__field--full">
      <p>Type or paste a project or community WebID.</p>  
      <div class="profile-edit-dialog__input-wrap">
          <input
            class="input profile-edit-dialog__input--with-action"
            type="text"
            name=${projectUrl}
            .value=${row?.url || ''}
            required
            data-contact-field="url"
            data-entry-node=${row?.entryNode || ''}
            data-row-status=${row?.status || 'n/a'}
            placeholder="Type or paste project/community WebID here"
            autocomplete="off"
            inputmode="text"
            @input=${handleUrlInput}
          />
          <button
            type="button"
            class="profile-edit-dialog__paste-button rounded-sm gap-xxs"
            aria-label="Paste project or community URL from clipboard"
            title=${_texts.pasteEntryButtonTitleText}
            @click=${handlePaste}
          >
            <span class="profile-edit-dialog__paste-icon" aria-hidden="true">${_profileIcons.pasteIcon}</span>
            Paste
          </button>
        </div>
      </label>
    </div>
  `;
}
function renderProjectsEditTemplate(form, formState, viewerMode) {
  const rerender = () => renderProjectsEditTemplate(form, formState, viewerMode);
  (0, _litHtml.render)((0, _litHtml.html)`
    ${renderProjectInputRow(formState.project, rerender)}
    ${viewerMode !== 'owner' ? (0, _litHtml.html)`<p class="profile-edit-dialog__login-message">${_texts.ownerLoginRequiredDialogMessageText}</p>` : null}
  `, form);
}
function createProjectsEditForm(details, viewerMode) {
  const form = document.createElement('form');
  form.classList.add('profile__edit-form');
  form.classList.add('profile__edit-form--projects');
  const formState = toFormState(details);
  renderProjectsEditTemplate(form, formState, viewerMode);
  return {
    form,
    formState
  };
}
async function createProjectsEditDialog(event, store, subject, projects, viewerMode, onSaved) {
  const dom = event.currentTarget?.ownerDocument || document;
  const {
    form,
    formState
  } = createProjectsEditForm(projects, viewerMode);
  const dialogPromise = (0, _dialog.openInputDialog)({
    title: _texts.editProjectsDialogTitleText,
    dom,
    form,
    headerAction: {
      type: 'close'
    },
    validate: () => {
      if (viewerMode !== 'owner') {
        return _texts.ownerLoginRequiredDialogMessageText;
      }
      return validateProjectBeforeSave(formState.project);
    },
    onSave: async () => {
      const projectOps = (0, _rowState.summarizeRowOps)([formState.project], rowHasContent);
      const plan = {
        create: projectOps.create,
        update: projectOps.update,
        remove: projectOps.remove
      };
      await (0, _mutations.processProjectsMutations)(store, subject, plan);
    },
    formatSaveError: error => {
      const message = error instanceof Error ? error.message : String(error);
      return `${_texts.saveProjectsUpdatesFailedPrefixText} ${message}`;
    }
  });
  const modalButtons = dom.querySelector('#profile-modal #modal-buttons');
  const previousButtonsDisplay = modalButtons?.style.display;
  if (modalButtons) {
    modalButtons.style.display = 'none';
  }
  let result = null;
  try {
    result = await dialogPromise;
  } finally {
    if (modalButtons) {
      modalButtons.style.display = previousButtonsDisplay || '';
    }
  }
  if (!result) return;
  if (onSaved) {
    await onSaved();
  }
}